'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import {
  canvasToBlob,
  createCleanorExportFileName,
  formatFileSize,
  getRenderScale,
  isPdfFile,
  openPdfDocument,
  renderPdfPageToCanvas,
  triggerBrowserDownload,
  type PdfJsDocument,
} from '@/lib/browser-pdf-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';

type RedactionRect = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fillColor: string;
};

type RedactThumb = {
  pageNumber: number;
  previewUrl: string;
};

type RedactSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
  thumbnails: RedactThumb[];
};

type DragDraft = {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
};

type ExportProgress = {
  current: number;
  total: number;
  label: string;
};

const redactionColors = [
  { id: 'black', label: 'Black', value: '#000000' },
  { id: 'white', label: 'White', value: '#ffffff' },
  { id: 'gray', label: 'Gray', value: '#6b7280' },
  { id: 'blue', label: 'Blue', value: '#2563eb' },
] as const;

const defaultRedactionColor = redactionColors[0];

function getRedactionColor(value: string) {
  return redactionColors.find((color) => color.value === value) ?? defaultRedactionColor;
}

async function createThumbs(file: File) {
  const document = await openPdfDocument(new Uint8Array(await file.arrayBuffer()));

  try {
    const thumbnails: RedactThumb[] = [];

    for (let pageIndex = 0; pageIndex < document.numPages; pageIndex += 1) {
      const pageNumber = pageIndex + 1;
      const page = await document.getPage(pageNumber);

      try {
        const baseViewport = page.getViewport({ scale: 1 });
        const previewScale = Math.max(
          0.52,
          Math.min(0.86, 190 / Math.max(baseViewport.width, baseViewport.height)),
        );
        const { canvas } = await renderPdfPageToCanvas(page, previewScale);
        const blob = await canvasToBlob(canvas, 'image/jpeg', 0.8);
        thumbnails.push({
          pageNumber,
          previewUrl: URL.createObjectURL(blob),
        });
        canvas.width = 0;
        canvas.height = 0;
      } finally {
        page.cleanup?.();
      }
    }

    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: document.numPages,
      thumbnails,
    };
  } catch {
    throw new Error(`"${file.name}" could not be opened. Use a standard unlocked PDF file.`);
  } finally {
    document.cleanup?.();
    await document.destroy?.();
  }
}

function createExportFileName(pageCount: number) {
  const pageLabel = pageCount === 1 ? '1-page' : `${pageCount}-pages`;
  return createCleanorExportFileName('redact-pdf', [pageLabel], 'pdf');
}

function getNormalizedRect(draft: DragDraft, bounds: DOMRect): RedactionRect | null {
  const left = Math.min(draft.startX, draft.currentX);
  const top = Math.min(draft.startY, draft.currentY);
  const width = Math.abs(draft.currentX - draft.startX);
  const height = Math.abs(draft.currentY - draft.startY);

  if (width < 12 || height < 12 || bounds.width <= 0 || bounds.height <= 0) {
    return null;
  }

  return {
    id: crypto.randomUUID(),
    x: Math.max(0, Math.min(1, left / bounds.width)),
    y: Math.max(0, Math.min(1, top / bounds.height)),
    width: Math.max(0.01, Math.min(1, width / bounds.width)),
    height: Math.max(0.01, Math.min(1, height / bounds.height)),
    fillColor: defaultRedactionColor.value,
  };
}

export default function BrowserRedactPdf() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const activeDocumentRef = useRef<PdfJsDocument | null>(null);
  const activePreviewUrlRef = useRef<string | null>(null);
  const [source, setSource] = useState<RedactSource | null>(null);
  const [activePageNumber, setActivePageNumber] = useState(1);
  const [activePreviewUrl, setActivePreviewUrl] = useState<string | null>(null);
  const [activePreviewSize, setActivePreviewSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [redactions, setRedactions] = useState<Record<number, RedactionRect[]>>({});
  const [dragDraft, setDragDraft] = useState<DragDraft | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isRenderingPreview, setIsRenderingPreview] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [selectedRedactionColor, setSelectedRedactionColor] = useState<string>(
    defaultRedactionColor.value,
  );

  const activeRedactions = redactions[activePageNumber] ?? [];
  const activeRedactionColor = getRedactionColor(selectedRedactionColor);

  const totalRedactions = useMemo(
    () => Object.values(redactions).reduce((sum, pageRects) => sum + pageRects.length, 0),
    [redactions],
  );

  useEffect(() => {
    return () => {
      source?.thumbnails.forEach((thumb) => URL.revokeObjectURL(thumb.previewUrl));

      if (activePreviewUrlRef.current) {
        URL.revokeObjectURL(activePreviewUrlRef.current);
      }

      void activeDocumentRef.current?.destroy?.();
      activeDocumentRef.current?.cleanup?.();
      activeDocumentRef.current = null;
    };
  }, [source]);

  useEffect(() => {
    if (!source) {
      setActivePreviewUrl(null);
      setActivePreviewSize(null);
      return;
    }

    let cancelled = false;

    async function renderActivePage(nextSource: RedactSource) {
      setIsRenderingPreview(true);
      setErrorMessage(null);

      try {
        if (!activeDocumentRef.current) {
          activeDocumentRef.current = await openPdfDocument(
            new Uint8Array(await nextSource.file.arrayBuffer()),
          );
        }

        const page = await activeDocumentRef.current.getPage(activePageNumber);

        try {
          const baseViewport = page.getViewport({ scale: 1 });
          const previewScale = getRenderScale(baseViewport, 1.28, 1280, 4_600_000);
          const { canvas, viewport } = await renderPdfPageToCanvas(page, previewScale);
          const blob = await canvasToBlob(canvas, 'image/jpeg', 0.88);
          const nextUrl = URL.createObjectURL(blob);
          canvas.width = 0;
          canvas.height = 0;

          if (cancelled) {
            URL.revokeObjectURL(nextUrl);
            return;
          }

          if (activePreviewUrlRef.current) {
            URL.revokeObjectURL(activePreviewUrlRef.current);
          }

          activePreviewUrlRef.current = nextUrl;
          setActivePreviewUrl(nextUrl);
          setActivePreviewSize({
            width: viewport.width,
            height: viewport.height,
          });
        } finally {
          page.cleanup?.();
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            error instanceof Error ? error.message : 'This page preview could not be prepared.',
          );
        }
      } finally {
        if (!cancelled) {
          setIsRenderingPreview(false);
        }
      }
    }

    void renderActivePage(source);

    return () => {
      cancelled = true;
    };
  }, [activePageNumber, source]);

  function resetEditorState() {
    setRedactions({});
    setActivePageNumber(1);
    setProgress(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    resetEditorState();

    if (!file) {
      setSource(null);
      return;
    }

    if (!isPdfFile(file)) {
      setSource(null);
      setErrorMessage('Choose one PDF file to redact.');
      return;
    }

    try {
      const nextSource = await createThumbs(file);
      setSource((current) => {
        current?.thumbnails.forEach((thumb) => URL.revokeObjectURL(thumb.previewUrl));
        return nextSource;
      });

      if (activePreviewUrlRef.current) {
        URL.revokeObjectURL(activePreviewUrlRef.current);
        activePreviewUrlRef.current = null;
      }

      activeDocumentRef.current?.cleanup?.();
      void activeDocumentRef.current?.destroy?.();
      activeDocumentRef.current = null;
    } catch (error) {
      setSource(null);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    }
  }

  function clearSource() {
    setSource((current) => {
      current?.thumbnails.forEach((thumb) => URL.revokeObjectURL(thumb.previewUrl));
      return null;
    });
    resetEditorState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (activePreviewUrlRef.current) {
      URL.revokeObjectURL(activePreviewUrlRef.current);
      activePreviewUrlRef.current = null;
    }

    activeDocumentRef.current?.cleanup?.();
    void activeDocumentRef.current?.destroy?.();
    activeDocumentRef.current = null;

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  function addRedactionRect(rect: RedactionRect) {
    setRedactions((current) => ({
      ...current,
      [activePageNumber]: [
        ...(current[activePageNumber] ?? []),
        { ...rect, fillColor: selectedRedactionColor },
      ],
    }));
  }

  function removeRedaction(pageNumber: number, rectId: string) {
    setRedactions((current) => ({
      ...current,
      [pageNumber]: (current[pageNumber] ?? []).filter((rect) => rect.id !== rectId),
    }));
  }

  function clearPageRedactions(pageNumber: number) {
    setRedactions((current) => ({
      ...current,
      [pageNumber]: [],
    }));
  }

  function getPointerOffsets(clientX: number, clientY: number) {
    const bounds = editorRef.current?.getBoundingClientRect();
    if (!bounds) {
      return null;
    }

    return {
      bounds,
      x: clientX - bounds.left,
      y: clientY - bounds.top,
    };
  }

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (!activePreviewUrl) {
      return;
    }

    const offsets = getPointerOffsets(event.clientX, event.clientY);
    if (!offsets) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    setDragDraft({
      startX: offsets.x,
      startY: offsets.y,
      currentX: offsets.x,
      currentY: offsets.y,
    });
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!dragDraft) {
      return;
    }

    const offsets = getPointerOffsets(event.clientX, event.clientY);
    if (!offsets) {
      return;
    }

    setDragDraft((current) =>
      current
        ? {
            ...current,
            currentX: offsets.x,
            currentY: offsets.y,
          }
        : null,
    );
  }

  function finishDraft() {
    if (!dragDraft || !editorRef.current) {
      setDragDraft(null);
      return;
    }

    const rect = getNormalizedRect(dragDraft, editorRef.current.getBoundingClientRect());
    if (rect) {
      addRedactionRect(rect);
    }

    setDragDraft(null);
  }

  async function exportRedactedPdf() {
    if (!source) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    if (!totalRedactions) {
      setErrorMessage('Add at least one redaction box before exporting.');
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setProgress({ current: 0, total: source.pageCount, label: 'Opening PDF…' });

    let document: PdfJsDocument | null = null;

    try {
      const credits = await consumeToolUsage(
        'redact-pdf',
        'Credits are not available for another PDF redaction export right now.',
      );

      document = await openPdfDocument(new Uint8Array(await source.file.arrayBuffer()));
      const exportDocument = await PDFDocument.create();

      for (let pageIndex = 0; pageIndex < source.pageCount; pageIndex += 1) {
        const pageNumber = pageIndex + 1;
        setProgress({
          current: pageNumber,
          total: source.pageCount,
          label: `Flattening redactions on page ${pageNumber} of ${source.pageCount}…`,
        });

        const page = await document.getPage(pageNumber);

        try {
          const baseViewport = page.getViewport({ scale: 1 });
          const renderScale = getRenderScale(baseViewport, 2);
          const { canvas, viewport } = await renderPdfPageToCanvas(page, renderScale);
          const context = canvas.getContext('2d');

          if (!context) {
            throw new Error('Your browser could not redraw the redacted page.');
          }

          const pageRects = redactions[pageNumber] ?? [];

          for (const rect of pageRects) {
            context.fillStyle = rect.fillColor;
            context.fillRect(
              rect.x * canvas.width,
              rect.y * canvas.height,
              rect.width * canvas.width,
              rect.height * canvas.height,
            );
          }

          const blob = await canvasToBlob(canvas, 'image/jpeg', 0.88);
          const image = await exportDocument.embedJpg(new Uint8Array(await blob.arrayBuffer()));
          const exportPage = exportDocument.addPage([viewport.width, viewport.height]);
          exportPage.drawImage(image, {
            x: 0,
            y: 0,
            width: viewport.width,
            height: viewport.height,
          });
          canvas.width = 0;
          canvas.height = 0;
        } finally {
          page.cleanup?.();
        }
      }

      const bytes = await exportDocument.save();
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setResultSize(blob.size);
      triggerBrowserDownload(url, createExportFileName(source.pageCount));
      URL.revokeObjectURL(url);
      setCreditMessage(formatToolCreditSuccess('Redacted PDF export', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'This PDF could not be redacted in the browser.',
      );
    } finally {
      setProgress(null);
      setIsExporting(false);
      document?.cleanup?.();
      await document?.destroy?.();
    }
  }

  const draftStyle =
    dragDraft && editorRef.current
      ? (() => {
          const bounds = editorRef.current.getBoundingClientRect();
          const rect = getNormalizedRect(dragDraft, bounds);
          if (!rect) {
            return null;
          }

          return {
            left: `${rect.x * 100}%`,
            top: `${rect.y * 100}%`,
            width: `${rect.width * 100}%`,
            height: `${rect.height * 100}%`,
          };
        })()
      : null;

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Redact a PDF in the browser</h2>
          <p>
            Draw black, white, gray, or blue redaction boxes on PDF pages, then export a flattened
            copy where the covered content is permanently removed from the visible result.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {source ? 'PDF ready for redaction' : 'Choose one PDF or drop it here'}
              </strong>
              <span>
                {source
                  ? `${source.pageCount} pages • ${formatFileSize(source.size)}`
                  : 'Best for scans, reports, and other documents where you need permanent visual redaction'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isExporting}
              onClick={() => void exportRedactedPdf()}
              type="button"
            >
              {isExporting ? 'Exporting redacted PDF…' : 'Export redacted PDF'}
            </button>
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear file
              </button>
            ) : null}
          </div>

          {progress ? (
            <div className="tool-pdf-progress">
              <span>{progress.label}</span>
              <progress max={progress.total} value={progress.current} />
            </div>
          ) : null}
          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Safe flattened redaction</h3>
          <div className="tool-mockup-chip-list">
            <span>PDF → PDF</span>
            <span>Page boxes</span>
            <span>{activeRedactionColor.label} boxes</span>
            <span>{totalRedactions} redactions</span>
            <span>Local export</span>
          </div>
          <p>
            The exported PDF is rebuilt page by page after the color boxes are applied, which keeps
            the redaction visual and permanent in the exported copy.
          </p>
          <div className="tool-live-panel-note">
            <strong>Important tradeoff</strong>
            <span>
              This browser-first version flattens each page into an image before export. It is safer
              for redaction, but searchable text, forms, and annotations can be flattened.
            </span>
          </div>
          {source ? (
            <div className="tool-live-stats">
              <div>
                <strong>Pages</strong>
                <span>{source.pageCount}</span>
              </div>
              <div>
                <strong>Active page</strong>
                <span>{activePageNumber}</span>
              </div>
              <div>
                <strong>Redactions</strong>
                <span>{totalRedactions}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Last export</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {source ? (
        <div className="tool-live-preview tool-redact-layout">
          <div>
            <h3>Pages</h3>
            <div className="tool-redact-thumbs">
              {source.thumbnails.map((thumb) => (
                <button
                  className={`tool-pdf-page-card tool-redact-thumb ${thumb.pageNumber === activePageNumber ? 'tool-redact-thumb-active' : ''}`}
                  key={thumb.pageNumber}
                  onClick={() => setActivePageNumber(thumb.pageNumber)}
                  type="button"
                >
                  <div className="tool-pdf-page-thumb">
                    <img alt="" src={thumb.previewUrl} />
                  </div>
                  <div className="tool-pdf-page-copy">
                    <strong>{`Page ${thumb.pageNumber}`}</strong>
                    <span>{`${(redactions[thumb.pageNumber] ?? []).length} redaction${(redactions[thumb.pageNumber] ?? []).length === 1 ? '' : 's'}`}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="tool-live-header">
              <div>
                <h3>Editor</h3>
                <p>Choose a fill color, then drag on the page preview to place a redaction box.</p>
              </div>
              {activeRedactions.length ? (
                <button
                  className="button button-ghost"
                  onClick={() => clearPageRedactions(activePageNumber)}
                  type="button"
                >
                  Clear page
                </button>
              ) : null}
            </div>

            <fieldset className="tool-redact-color-picker">
              <legend>Redaction color</legend>
              <div className="tool-redact-color-options">
                {redactionColors.map((color) => (
                  <label
                    className={`tool-redact-color-option ${
                      selectedRedactionColor === color.value
                        ? 'tool-redact-color-option-active'
                        : ''
                    }`}
                    key={color.id}
                  >
                    <input
                      checked={selectedRedactionColor === color.value}
                      name="redaction-color"
                      onChange={() => setSelectedRedactionColor(color.value)}
                      type="radio"
                      value={color.value}
                    />
                    <span
                      aria-hidden="true"
                      className="tool-redact-color-swatch"
                      style={{
                        backgroundColor: color.value,
                        borderColor: color.value,
                      }}
                    />
                    <span>{color.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div
              className="tool-redact-editor"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={finishDraft}
              onPointerCancel={finishDraft}
              ref={editorRef}
            >
              {activePreviewUrl ? (
                <>
                  <img alt="" src={activePreviewUrl} />
                  <div className="tool-redact-overlay">
                    {activeRedactions.map((rect) => (
                      <button
                        className="tool-redact-box"
                        key={rect.id}
                        onClick={() => removeRedaction(activePageNumber, rect.id)}
                        onPointerDown={(event) => event.stopPropagation()}
                        style={{
                          left: `${rect.x * 100}%`,
                          top: `${rect.y * 100}%`,
                          width: `${rect.width * 100}%`,
                          height: `${rect.height * 100}%`,
                          backgroundColor: rect.fillColor,
                          borderColor: rect.fillColor,
                        }}
                        title="Remove redaction"
                        type="button"
                      />
                    ))}
                    {draftStyle ? (
                      <div
                        className="tool-redact-box tool-redact-box-draft"
                        style={{
                          ...draftStyle,
                          backgroundColor: selectedRedactionColor,
                          borderColor: selectedRedactionColor,
                        }}
                      />
                    ) : null}
                  </div>
                </>
              ) : isRenderingPreview ? (
                <div className="tool-editor-empty">
                  <strong>Preparing page preview…</strong>
                </div>
              ) : (
                <div className="tool-editor-empty">
                  <strong>Select a page to start redacting.</strong>
                </div>
              )}
            </div>

            {activePreviewSize ? (
              <p className="tool-redact-meta">
                {`Preview size ${Math.round(activePreviewSize.width)} × ${Math.round(activePreviewSize.height)} • click a redaction box to remove it`}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </article>
  );
}
